<template>
  <div class="bigdata">
    <!-- 顶部标题 -->
    <div class="bd-head">
      <div class="bd-title-wrap">
        <span class="bd-deco"></span>
        <h1 class="bd-title">小义 AI 教育工具平台 · 数据大盘</h1>
        <span class="bd-deco"></span>
      </div>
      <div class="bd-head-right">
        <span class="bd-clock">{{ clock }}</span>
        <button class="bd-btn" :disabled="loading" @click="load">
          <i class="fa" :class="loading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
          {{ loading ? "刷新中" : "刷新" }}
        </button>
      </div>
    </div>

    <!-- KPI -->
    <div class="bd-kpis">
      <div v-for="k in kpis" :key="k.label" class="bd-kpi">
        <div class="bd-kpi-label">
          <i class="fa" :class="k.icon"></i>{{ k.label }}
        </div>
        <div class="bd-kpi-value">
          {{ k.value }}<span class="bd-kpi-unit">{{ k.unit }}</span>
        </div>
        <div class="bd-kpi-sub">{{ k.sub }}</div>
      </div>
    </div>

    <!-- 主区 -->
    <div class="bd-grid">
      <!-- 趋势 -->
      <section class="bd-card bd-col-2">
        <header class="bd-card-head">
          <h2>近 14 天访问趋势</h2>
          <div class="bd-legend">
            <span v-for="s in trendSeries" :key="s.name">
              <i :style="{ background: s.color }"></i>{{ s.name }}
            </span>
          </div>
        </header>
        <div class="bd-chart">
          <svg :viewBox="`0 0 ${W} ${H}`" class="bd-svg">
            <g v-for="(g, i) in gridLines" :key="'g' + i">
              <line :x1="padL" :y1="g.y" :x2="W - padR" :y2="g.y" stroke="rgba(56,189,248,.14)" />
              <text :x="padL - 6" :y="g.y + 4" text-anchor="end" class="bd-axis">{{ g.v }}</text>
            </g>
            <g v-for="s in trendSeries" :key="s.name">
              <path :d="s.path" fill="none" :stroke="s.color" stroke-width="2" stroke-linejoin="round" />
              <circle
                v-for="(p, pi) in s.points"
                :key="s.name + pi"
                :cx="p.x"
                :cy="p.y"
                r="2.4"
                :fill="s.color"
              />
            </g>
          </svg>
          <div class="bd-xaxis">
            <span v-for="(d, i) in trendDays" :key="i">{{ d }}</span>
          </div>
        </div>
      </section>

      <!-- 内容分布 -->
      <section class="bd-card">
        <header class="bd-card-head">
          <h2>内容分布</h2>
          <span class="bd-tip">按数量</span>
        </header>
        <div class="bd-bars">
          <div v-for="c in contents" :key="c.name" class="bd-bar-row">
            <span class="bd-bar-name">{{ c.name }}</span>
            <div class="bd-bar-track">
              <div class="bd-bar-fill" :style="{ width: barWidth(c.total, contentMax) }"></div>
            </div>
            <span class="bd-bar-val">{{ c.total }}</span>
          </div>
        </div>
      </section>

      <!-- 地域分布 -->
      <section class="bd-card">
        <header class="bd-card-head">
          <h2>用户地域分布</h2>
          <span class="bd-tip">Top 12 省份</span>
        </header>
        <div v-if="regions.length" class="bd-bars">
          <div v-for="r in regions" :key="r.name" class="bd-bar-row">
            <span class="bd-bar-name bd-ellipsis">{{ r.name }}</span>
            <div class="bd-bar-track">
              <div class="bd-bar-fill bd-bar-cyan" :style="{ width: barWidth(r.value, regionMax) }"></div>
            </div>
            <span class="bd-bar-val">{{ r.value }}</span>
          </div>
        </div>
        <p v-else class="bd-empty">暂无地域数据</p>
      </section>

      <!-- 内容明细 -->
      <section class="bd-card bd-col-2">
        <header class="bd-card-head">
          <h2>各模块内容与互动</h2>
          <span class="bd-tip">浏览＝详情页累计打开次数</span>
        </header>
        <div class="bd-table-wrap">
          <table class="bd-table">
            <thead>
              <tr>
                <th>模块</th>
                <th>数量</th>
                <th>浏览</th>
                <th>下载</th>
                <th>互动</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in contents" :key="c.name">
                <td>{{ c.name }}</td>
                <td class="bd-num">{{ c.total }}</td>
                <td class="bd-num">{{ fmt(c.view) }}</td>
                <td class="bd-num">{{ fmt(c.download) }}</td>
                <td class="bd-num">{{ fmt(c.interact) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 浏览排行 -->
      <section class="bd-card">
        <header class="bd-card-head">
          <h2>工具浏览排行</h2>
          <span class="bd-tip">Top 10</span>
        </header>
        <div v-if="toolViews.length" class="bd-rank">
          <div v-for="(t, i) in toolViews" :key="'v' + i" class="bd-rank-row">
            <span class="bd-rank-no" :class="'no' + (i + 1)">{{ i + 1 }}</span>
            <div class="bd-rank-main">
              <div class="bd-rank-title bd-ellipsis">{{ t.name }}</div>
              <div class="bd-bar-track">
                <div class="bd-bar-fill" :style="{ width: barWidth(t.value, toolViewMax) }"></div>
              </div>
            </div>
            <span class="bd-rank-val">{{ fmt(t.value) }}</span>
          </div>
        </div>
        <p v-else class="bd-empty">暂无数据</p>
      </section>

      <!-- 下载排行 -->
      <section class="bd-card">
        <header class="bd-card-head">
          <h2>工具下载排行</h2>
          <span class="bd-tip">Top 10</span>
        </header>
        <div v-if="toolDownloads.length" class="bd-rank">
          <div v-for="(t, i) in toolDownloads" :key="'d' + i" class="bd-rank-row">
            <span class="bd-rank-no" :class="'no' + (i + 1)">{{ i + 1 }}</span>
            <div class="bd-rank-main">
              <div class="bd-rank-title bd-ellipsis">{{ t.name }}</div>
              <div class="bd-bar-track">
                <div class="bd-bar-fill bd-bar-amber" :style="{ width: barWidth(t.value, toolDownMax) }"></div>
              </div>
            </div>
            <span class="bd-rank-val">{{ fmt(t.value) }}</span>
          </div>
        </div>
        <p v-else class="bd-empty">暂无数据</p>
      </section>

      <!-- 活跃用户 -->
      <section class="bd-card">
        <header class="bd-card-head">
          <h2>活跃用户</h2>
          <span class="bd-tip">累计在线时长</span>
        </header>
        <div v-if="activeUsers.length" class="bd-rank">
          <div v-for="(u, i) in activeUsers" :key="'u' + i" class="bd-rank-row">
            <span class="bd-rank-no" :class="'no' + (i + 1)">{{ i + 1 }}</span>
            <div class="bd-rank-main">
              <div class="bd-rank-title bd-ellipsis">{{ u.name }}</div>
              <div class="bd-bar-track">
                <div class="bd-bar-fill bd-bar-green" :style="{ width: barWidth(u.value, activeMax) }"></div>
              </div>
            </div>
            <span class="bd-rank-val">{{ u.value }} 分</span>
          </div>
        </div>
        <p v-else class="bd-empty">暂无数据</p>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: "BigDataPanel",
  data() {
    return {
      loading: false,
      clock: "",
      timer: null,
      d: {
        kpi: {},
        trend: { days: [], login: [], play: [], download: [], comment: [], newUser: [], newTool: [] },
        regions: [],
        contents: [],
        toolViews: [],
        toolDownloads: [],
        activeUsers: [],
      },
      W: 760,
      H: 230,
      padL: 40,
      padR: 12,
      padT: 14,
      padB: 18,
    };
  },
  computed: {
    kpi() {
      return this.d.kpi || {};
    },
    contents() {
      return this.d.contents || [];
    },
    regions() {
      return this.d.regions || [];
    },
    toolViews() {
      return this.d.toolViews || [];
    },
    toolDownloads() {
      return this.d.toolDownloads || [];
    },
    activeUsers() {
      return this.d.activeUsers || [];
    },
    trendDays() {
      return (this.d.trend && this.d.trend.days) || [];
    },
    contentMax() {
      return Math.max(1, ...this.contents.map((c) => Number(c.total) || 0));
    },
    regionMax() {
      return Math.max(1, ...this.regions.map((r) => Number(r.value) || 0));
    },
    toolViewMax() {
      return Math.max(1, ...this.toolViews.map((t) => Number(t.value) || 0));
    },
    toolDownMax() {
      return Math.max(1, ...this.toolDownloads.map((t) => Number(t.value) || 0));
    },
    activeMax() {
      return Math.max(1, ...this.activeUsers.map((u) => Number(u.value) || 0));
    },
    kpis() {
      const k = this.kpi;
      const contentTotal =
        (k.toolTotal || 0) + (k.caseTotal || 0) + (k.softwareTotal || 0) +
        (k.webTotal || 0) + (k.promptTotal || 0) + (k.demandTotal || 0);
      return [
        {
          label: "用户总数",
          icon: "fa-users",
          value: this.fmt(k.userTotal),
          unit: "人",
          sub: `待审核 ${this.fmt(k.userPending)} · 管理员 ${this.fmt(k.adminTotal)}`,
        },
        {
          label: "内容总量",
          icon: "fa-cubes",
          value: this.fmt(contentTotal),
          unit: "件",
          sub: `工具 ${this.fmt(k.toolTotal)} · 案例 ${this.fmt(k.caseTotal)} · 软件 ${this.fmt(k.softwareTotal)}`,
        },
        {
          label: "累计浏览",
          icon: "fa-eye",
          value: this.fmt(k.viewTotal),
          unit: "次",
          sub: "工具 / 软件 / 技巧详情页打开",
        },
        {
          label: "累计下载",
          icon: "fa-download",
          value: this.fmt(k.downloadTotal),
          unit: "次",
          sub: "工具 + 软件",
        },
        {
          label: "互动总数",
          icon: "fa-comments-o",
          value: this.fmt(k.interactTotal),
          unit: "条",
          sub: `赞 ${this.fmt(k.zanTotal)} · 藏 ${this.fmt(k.favTotal)} · 评 ${this.fmt(k.commentTotal)}`,
        },
        {
          label: "待审核工具",
          icon: "fa-clock-o",
          value: this.fmt(k.toolPending),
          unit: "个",
          sub: `已上架 ${this.fmt(k.toolOnline)} 个`,
        },
      ];
    },
    gridLines() {
      const max = this.trendMax;
      const out = [];
      for (let i = 0; i <= 4; i++) {
        const v = Math.round((max / 4) * i);
        out.push({ v, y: this.scaleY(v) });
      }
      return out;
    },
    trendMax() {
      const t = this.d.trend || {};
      const all = [].concat(t.login || [], t.play || [], t.download || [], t.newUser || []);
      return Math.max(1, ...all.map((n) => Number(n) || 0));
    },
    trendSeries() {
      const t = this.d.trend || {};
      const defs = [
        { name: "登录", key: "login", color: "#22d3ee" },
        { name: "在线使用", key: "play", color: "#38bdf8" },
        { name: "下载", key: "download", color: "#fbbf24" },
        { name: "新增用户", key: "newUser", color: "#34d399" },
      ];
      return defs
        .map((d) => {
          const vals = (t[d.key] || []).map((n) => Number(n) || 0);
          if (!vals.length) return null;
          const n = vals.length;
          const pts = vals.map((v, i) => ({
            x: this.padL + (n === 1 ? 0 : (i * (this.W - this.padL - this.padR)) / (n - 1)),
            y: this.scaleY(v),
          }));
          return { ...d, points: pts, path: this.toPath(pts) };
        })
        .filter(Boolean);
    },
  },
  created() {
    this.tick();
    this.timer = setInterval(this.tick, 1000);
    this.load();
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    tick() {
      const now = new Date();
      const p = (n) => String(n).padStart(2, "0");
      this.clock = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())} ${p(
        now.getHours()
      )}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios({ url: "bigdata/overview", method: "GET" });
        if (res.code === 200 && res.data) {
          this.d = Object.assign({}, this.d, res.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    scaleY(v) {
      const span = this.H - this.padT - this.padB;
      const max = this.trendMax;
      return this.padT + (1 - (Number(v) || 0) / max) * span;
    },
    toPath(pts) {
      return pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
    },
    barWidth(v, max) {
      const pct = Math.min(100, ((Number(v) || 0) / (max || 1)) * 100);
      return pct <= 0 ? "2px" : pct + "%";
    },
    fmt(v) {
      const n = Number(v) || 0;
      if (n >= 100000) return (n / 10000).toFixed(1) + " 万";
      return n.toLocaleString("en-US");
    },
  },
};
</script>

<style scoped>
.bigdata {
  background: radial-gradient(circle at 50% 0%, #0b2138 0%, #050b18 55%, #03070f 100%);
  border: 1px solid rgba(34, 211, 238, 0.18);
  border-radius: 10px;
  padding: 14px;
  color: #cbe6f6;
  font-variant-numeric: tabular-nums;
}

/* 顶部 */
.bd-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(34, 211, 238, 0.16);
}
.bd-title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.bd-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #e6f7ff;
  text-shadow: 0 0 12px rgba(34, 211, 238, 0.55);
}
.bd-deco {
  width: 42px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #22d3ee);
}
.bd-deco:last-child {
  background: linear-gradient(90deg, #22d3ee, transparent);
}
.bd-head-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.bd-clock {
  font-size: 12px;
  color: #67e8f9;
  letter-spacing: 1px;
}
.bd-btn {
  font-size: 12px;
  color: #a5f3fc;
  border: 1px solid rgba(34, 211, 238, 0.35);
  padding: 4px 10px;
  border-radius: 4px;
  background: rgba(8, 47, 73, 0.5);
}
.bd-btn:hover {
  background: rgba(8, 47, 73, 0.9);
}

/* KPI */
.bd-kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin: 12px 0;
}
@media (max-width: 1280px) {
  .bd-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 640px) {
  .bd-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.bd-kpi {
  position: relative;
  background: linear-gradient(180deg, rgba(12, 44, 73, 0.75), rgba(6, 20, 36, 0.75));
  border: 1px solid rgba(34, 211, 238, 0.22);
  border-radius: 6px;
  padding: 10px 12px;
  overflow: hidden;
}
.bd-kpi::after {
  content: "";
  position: absolute;
  right: -20px;
  top: -20px;
  width: 60px;
  height: 60px;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.25), transparent 70%);
}
.bd-kpi-label {
  font-size: 12px;
  color: #7dd3fc;
  display: flex;
  align-items: center;
  gap: 6px;
}
.bd-kpi-value {
  margin-top: 6px;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  color: #67e8f9;
  text-shadow: 0 0 14px rgba(34, 211, 238, 0.45);
}
.bd-kpi-unit {
  font-size: 12px;
  margin-left: 4px;
  color: #7dd3fc;
}
.bd-kpi-sub {
  margin-top: 4px;
  font-size: 11px;
  color: #64809a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 主区 */
.bd-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
@media (max-width: 1280px) {
  .bd-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 768px) {
  .bd-grid {
    grid-template-columns: 1fr;
  }
}
.bd-col-2 {
  grid-column: span 2;
}
@media (max-width: 768px) {
  .bd-col-2 {
    grid-column: span 1;
  }
}
.bd-card {
  background: linear-gradient(180deg, rgba(9, 32, 55, 0.85), rgba(5, 15, 28, 0.85));
  border: 1px solid rgba(34, 211, 238, 0.18);
  border-radius: 6px;
  padding: 10px 12px;
}
.bd-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(34, 211, 238, 0.12);
  margin-bottom: 8px;
}
.bd-card-head h2 {
  font-size: 13px;
  font-weight: 600;
  color: #a5f3fc;
  letter-spacing: 1px;
}
.bd-tip {
  font-size: 11px;
  color: #5b7a94;
}
.bd-legend {
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: #7dd3fc;
}
.bd-legend i {
  display: inline-block;
  width: 10px;
  height: 3px;
  margin-right: 4px;
  vertical-align: middle;
  border-radius: 2px;
}

/* 折线 */
.bd-chart {
  width: 100%;
}
.bd-svg {
  width: 100%;
  height: auto;
  display: block;
}
.bd-axis {
  font-size: 9px;
  fill: #4f7691;
}
.bd-xaxis {
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  color: #4f7691;
  padding: 0 8px;
}

/* 条形 */
.bd-bars {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.bd-bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}
.bd-bar-name {
  width: 68px;
  flex-shrink: 0;
  color: #9fc7dd;
}
.bd-bar-track {
  flex: 1;
  height: 8px;
  background: rgba(56, 189, 248, 0.12);
  border-radius: 4px;
  overflow: hidden;
}
.bd-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #0ea5e9, #22d3ee);
  transition: width 0.4s ease;
}
.bd-bar-cyan {
  background: linear-gradient(90deg, #0891b2, #67e8f9);
}
.bd-bar-amber {
  background: linear-gradient(90deg, #b45309, #fbbf24);
}
.bd-bar-green {
  background: linear-gradient(90deg, #047857, #34d399);
}
.bd-bar-val {
  width: 52px;
  text-align: right;
  color: #67e8f9;
  font-size: 11px;
}

/* 表 */
.bd-table-wrap {
  overflow-x: auto;
}
.bd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.bd-table th {
  text-align: left;
  font-weight: 400;
  color: #5b7a94;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(34, 211, 238, 0.15);
  white-space: nowrap;
}
.bd-table td {
  padding: 6px 8px;
  border-bottom: 1px solid rgba(34, 211, 238, 0.07);
  color: #b6d8ea;
  white-space: nowrap;
}
.bd-num {
  text-align: right;
  color: #67e8f9;
}

/* 排行 */
.bd-rank {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bd-rank-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}
.bd-rank-no {
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  font-size: 10px;
  border-radius: 3px;
  background: rgba(56, 189, 248, 0.15);
  color: #7dd3fc;
  flex-shrink: 0;
}
.bd-rank-no.no1 {
  background: #fbbf24;
  color: #1f1200;
}
.bd-rank-no.no2 {
  background: #94a3b8;
  color: #0b1220;
}
.bd-rank-no.no3 {
  background: #b45309;
  color: #fff7ed;
}
.bd-rank-main {
  flex: 1;
  min-width: 0;
}
.bd-rank-title {
  color: #b6d8ea;
  margin-bottom: 3px;
}
.bd-rank-val {
  width: 62px;
  text-align: right;
  color: #67e8f9;
  font-size: 11px;
  flex-shrink: 0;
}
.bd-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bd-empty {
  font-size: 12px;
  color: #4f7691;
  padding: 12px 0;
  text-align: center;
}
</style>
